import type {
  ErrorMessagesMap,
  FieldChangeTriggerHandler,
  FormChangeHandler,
  FormData,
  GetValidationErrorKey,
  HasErrorMap,
  ValidatedFieldChangeHandler,
  ValidationMap,
} from '../../types/form';
import type { SetStateInitialValue } from '../state-map';
import { useStateMap } from '../state-map';
import { useFieldChangeHandler } from './form-handlers';

interface UseFormOptions<FieldName extends string> {
  initialFormData?: SetStateInitialValue<FormData<FieldName>>;
  initialErrorMessagesMap?: SetStateInitialValue<ErrorMessagesMap<FieldName>>;
  initialHasErrorMap?: SetStateInitialValue<HasErrorMap<FieldName>>;
  validationMap: ValidationMap<FieldName>;
  partialValidationOnChange?: boolean;
  getValidationErrorKey?: GetValidationErrorKey<FieldName>;
  onFormChange?: FormChangeHandler<FieldName>;
}

interface UseFormFieldsResult<FieldName extends string> {
  formData: FormData<FieldName>;
  hasErrorMap: HasErrorMap<FieldName>;
  errorMessagesMap: ErrorMessagesMap<FieldName>;
  onFieldChange: ValidatedFieldChangeHandler<FieldName>;
}

interface UseFormStateOptions<FieldName extends string> {
  initialFormData?: SetStateInitialValue<FormData<FieldName>>;
  initialErrorMessagesMap?: SetStateInitialValue<ErrorMessagesMap<FieldName>>;
  initialHasErrorMap?: SetStateInitialValue<HasErrorMap<FieldName>>;
  onFormChange?: FormChangeHandler<FieldName>;
  isBillingSubForm?: boolean;
}

interface UseFormResult<FieldName extends string> {
  formData: FormData<FieldName>;
  hasErrorMap: HasErrorMap<FieldName>;
  errorMessagesMap: ErrorMessagesMap<FieldName>;
  handleInputChange: FieldChangeTriggerHandler<FieldName>;
}

export const useFormState = <FieldName extends string>({
  initialFormData,
  initialErrorMessagesMap,
  initialHasErrorMap,
  onFormChange,
}: UseFormStateOptions<FieldName>): UseFormFieldsResult<FieldName> => {
  const [formData, setFieldValue] = useStateMap(initialFormData);
  const [hasErrorMap, setFieldHasError] = useStateMap(initialHasErrorMap);
  const [errorMessagesMap, setFieldErrorMessages] = useStateMap(initialErrorMessagesMap);
  const onFieldChange: ValidatedFieldChangeHandler<FieldName> = ({
    fieldName,
    value,
    valid,
    errors: errorMessageList,
  }) => {
    const updatedFormData = setFieldValue(fieldName, value);
    const updatedErrorsMap = setFieldErrorMessages(fieldName, errorMessageList);
    const updatedHasErrorsMap = setFieldHasError(fieldName, !valid);

    if (onFormChange) {
      onFormChange({
        formData: updatedFormData,
        errorMessagesMap: updatedErrorsMap,
        hasErrorMap: updatedHasErrorsMap,
      });
    }
  };

  return {
    formData,
    hasErrorMap,
    errorMessagesMap,
    onFieldChange,
  };
};

export const useForm = <FieldName extends string>({
  initialFormData,
  initialErrorMessagesMap,
  initialHasErrorMap,
  validationMap,
  getValidationErrorKey,
  partialValidationOnChange,
  onFormChange,
}: UseFormOptions<FieldName>): UseFormResult<FieldName> => {
  const { onFieldChange, ...formState } = useFormState({
    initialFormData,
    initialErrorMessagesMap,
    initialHasErrorMap,
    onFormChange,
  });
  const handleInputChange = useFieldChangeHandler<FieldName>({
    onFieldChange,
    validationMap,
    formData: formState.formData,
    errorMessagesMap: formState.errorMessagesMap,
    partialValidationOnChange,
    getValidationErrorKey,
  });

  return {
    ...formState,
    handleInputChange,
  };
};
